import { useEffect, useState, type FormEvent } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import {
  Badge,
  Banner,
  Breadcrumbs,
  Button,
  Empty,
  Grid,
  GridItem,
  Input,
  Label,
  LayerCard,
  Loader,
  Switch,
  Table,
  Text,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { House, UserPlus } from '@phosphor-icons/react'
import type { GuestInvitationDto, RecentGuest } from '@hub-wifi/shared'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { ApiError } from '../api/client'
import {
  createGuestInvitation,
  listGuestInvitations,
  listRecentGuests,
} from '../api/guestInvitations'

interface FormState {
  guestName: string
  guestEmail: string
  guestPhone: string
  visitDate: string
  visitTimeBegin: string
  visitTimeEnd: string
  singleAccess: boolean
}

/**
 * Default visit date for the form:
 * - Inside business hours (08:00 ≤ now < 20:00): today.
 * - Outside business hours (early morning, evening, night): next workweek
 *   day (skip Saturday and Sunday). Resolves to Monday on a Friday night
 *   or any time during the weekend.
 *
 * Computed in the user's local timezone — the same frame the form's date
 * input renders in. The API's BUILDING-timezone validation runs server-side
 * and may still reject same-day past times if the building TZ differs.
 */
function defaultVisitDate(now: Date = new Date()): string {
  const hour = now.getHours()
  const inBusinessHours = hour >= 8 && hour < 20

  const target = new Date(now)
  if (!inBusinessHours) {
    // Move to tomorrow, then skip past Sat (6) and Sun (0).
    target.setDate(target.getDate() + 1)
    while (target.getDay() === 0 || target.getDay() === 6) {
      target.setDate(target.getDate() + 1)
    }
  }
  // Format as YYYY-MM-DD in local time (NOT toISOString, which is UTC).
  const yyyy = target.getFullYear()
  const mm = String(target.getMonth() + 1).padStart(2, '0')
  const dd = String(target.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function buildEmptyForm(): FormState {
  return {
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    visitDate: defaultVisitDate(),
    visitTimeBegin: '09:00',
    visitTimeEnd: '18:00',
    singleAccess: false,
  }
}

export function GuestInvitationsPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()

  const [recent, setRecent] = useState<RecentGuest[]>([])
  const [list, setList] = useState<GuestInvitationDto[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [form, setForm] = useState<FormState>(() => buildEmptyForm())
  const [createdInvitation, setCreatedInvitation] = useState<GuestInvitationDto | null>(null)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      try {
        const token = await getAccessTokenSilently()
        const [recentRes, listRes] = await Promise.all([
          listRecentGuests(token),
          listGuestInvitations(token),
        ])
        setRecent(recentRes)
        setList(listRes)
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'GENERIC_ERROR')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [getAccessTokenSilently])

  function applyRecent(g: RecentGuest) {
    setForm((f) => ({
      ...f,
      guestName: g.guestName,
      guestEmail: g.guestEmail,
      guestPhone: g.guestPhone ?? '',
    }))
  }

  async function copyLink(url: string, key: string) {
    // TODO(review): wrap navigator.clipboard.writeText in try/catch and surface failure to user (reported by ring:security-reviewer on 2026-05-03, severity: Medium)
    await navigator.clipboard.writeText(url)
    setCopiedToken(key)
    window.setTimeout(() => setCopiedToken(null), 2000)
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault()
    setSubmitting(true)
    try {
      const token = await getAccessTokenSilently()
      const dto = await createGuestInvitation(
        {
          guestName: form.guestName,
          guestEmail: form.guestEmail || null,
          guestPhone: form.guestPhone || null,
          visitDate: form.visitDate,
          visitTimeBegin: form.visitTimeBegin,
          visitTimeEnd: form.visitTimeEnd,
          singleAccess: form.singleAccess,
        },
        token,
      )
      setCreatedInvitation(dto)
      setList((prev) =>
        [...prev, dto].sort((a, b) =>
          a.visitDate === b.visitDate
            ? a.visitTimeBegin.localeCompare(b.visitTimeBegin)
            : a.visitDate.localeCompare(b.visitDate),
        ),
      )
      setForm(buildEmptyForm())
      // Refresh recent guests if we used an email.
      if (dto.guestEmail) {
        const newRecent = await listRecentGuests(token)
        setRecent(newRecent)
      }
    } catch (err) {
      let description: string
      if (err instanceof ApiError) {
        const code = (err.body as { code?: string } | undefined)?.code ?? 'TRUESAFE_UNAVAILABLE'
        description = t(`guestInvitations.errors.${code}`) || err.message
      } else {
        description = t('common.error')
      }
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description,
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<House size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('guestInvitations.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('guestInvitations.title')}
        description={t('guestInvitations.description')}
      />

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && loadError && (
        <Banner variant="error" className="mb-4">
          {t('common.errorPrefix')} {loadError === 'GENERIC_ERROR' ? t('common.error') : loadError}
        </Banner>
      )}

      {!loading && (
        <>
          <Grid variant="2up" gap="base" className="mb-6">
            <GridItem>
              {/* upcoming guest invitations */}
              {(() => {
                const todayIso = new Date().toISOString().slice(0, 10)
                const upcoming = list.filter((inv) => inv.visitDate >= todayIso)
                return (
                  <LayerCard>
                    <LayerCard.Secondary>
                      <span className="text-sm font-medium">
                        {t('guestInvitations.list.title')}
                      </span>
                    </LayerCard.Secondary>
                    <LayerCard.Primary className={upcoming.length === 0 ? '' : 'p-0'}>
                      {upcoming.length === 0 ? (
                        <Empty title={t('guestInvitations.list.empty')} />
                      ) : (
                        <Table>
                          <Table.Header>
                            <Table.Row>
                              <Table.Head>{t('guestInvitations.form.guestName')}</Table.Head>
                              <Table.Head>{t('guestInvitations.form.visitDate')}</Table.Head>
                              <Table.Head>{t('guestInvitations.form.visitTimeBegin')}</Table.Head>
                              <Table.Head>{t('guestInvitations.form.visitTimeEnd')}</Table.Head>
                              <Table.Head />
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {upcoming.map((row) => (
                              <Table.Row key={row.id}>
                                <Table.Cell>
                                  {row.guestName}
                                  {row.singleAccess && (
                                    <Badge className="ml-2" variant="secondary">
                                      1
                                    </Badge>
                                  )}
                                </Table.Cell>
                                <Table.Cell>{row.visitDate}</Table.Cell>
                                <Table.Cell>{row.visitTimeBegin}</Table.Cell>
                                <Table.Cell>{row.visitTimeEnd}</Table.Cell>
                                <Table.Cell>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => copyLink(row.invitationUrl, row.id)}
                                  >
                                    {copiedToken === row.id
                                      ? t('guestInvitations.success.copied')
                                      : t('guestInvitations.list.copyLink')}
                                  </Button>
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table>
                      )}
                    </LayerCard.Primary>
                  </LayerCard>
                )
              })()}

              {/* recent guest invitations */}
              <LayerCard className="mt-6">
                <LayerCard.Secondary>
                  <span className="text-sm font-medium">{t('guestInvitations.recent.title')}</span>
                </LayerCard.Secondary>
                <LayerCard.Primary>
                  {recent.length === 0 ? (
                    <Empty
                      title={t('guestInvitations.recent.empty')}
                      description={t('guestInvitations.recent.emptyDescription')}
                    />
                  ) : (
                    <ul className="flex flex-col gap-1.5">
                      {recent.map((g) => (
                        <li
                          key={g.guestEmail}
                          className="flex items-center justify-between gap-2 rounded-md border border-kumo-line px-3 py-2"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{g.guestName}</div>
                            <div className="text-xs opacity-70 truncate">{g.guestEmail}</div>
                          </div>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="shrink-0"
                            onClick={() => applyRecent(g)}
                          >
                            {t('guestInvitations.recent.reinvite')}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </LayerCard.Primary>
              </LayerCard>
            </GridItem>

            <GridItem>
              <LayerCard>
                <LayerCard.Secondary>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                    <UserPlus size={14} />
                    {t('guestInvitations.form.title')}
                  </span>
                </LayerCard.Secondary>
                <LayerCard.Primary>
                  <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-2" noValidate>
                    <div className="flex flex-col gap-1 sm:col-span-2">
                      <Label>{t('guestInvitations.form.guestName')}</Label>
                      <Input
                        required
                        value={form.guestName}
                        onChange={(e) => setForm((f) => ({ ...f, guestName: e.target.value }))}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label>{t('guestInvitations.form.guestEmail')}</Label>
                      <Input
                        type="email"
                        value={form.guestEmail}
                        onChange={(e) => setForm((f) => ({ ...f, guestEmail: e.target.value }))}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label>{t('guestInvitations.form.guestPhone')}</Label>
                      <Input
                        type="tel"
                        placeholder="+5511999990000"
                        value={form.guestPhone}
                        onChange={(e) => setForm((f) => ({ ...f, guestPhone: e.target.value }))}
                      />
                    </div>
                    <Text as="p" size="xs" DANGEROUS_className="opacity-70 sm:col-span-2">
                      {t('guestInvitations.form.contactNotice')}
                    </Text>
                    <div className="grid grid-cols-3 md:grid-cols-3 gap-1 sm:col-span-2">
                      <div className="flex flex-col gap-1">
                        <Label>{t('guestInvitations.form.visitDate')}</Label>
                        <Input
                          type="date"
                          required
                          value={form.visitDate}
                          onChange={(e) => setForm((f) => ({ ...f, visitDate: e.target.value }))}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label>{t('guestInvitations.form.visitTimeBegin')}</Label>
                        {/* FIXME(nitpick): Kumo Input lacks type="time" support — using native input (reported by ring:code-reviewer on 2026-05-03, severity: Medium) */}
                        <input
                          type="time"
                          required
                          value={form.visitTimeBegin}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, visitTimeBegin: e.target.value }))
                          }
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label>{t('guestInvitations.form.visitTimeEnd')}</Label>
                        {/* FIXME(nitpick): Kumo Input lacks type="time" support — using native input (reported by ring:code-reviewer on 2026-05-03, severity: Medium) */}
                        <input
                          type="time"
                          required
                          value={form.visitTimeEnd}
                          onChange={(e) => setForm((f) => ({ ...f, visitTimeEnd: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Switch
                        label={t('guestInvitations.form.singleAccess')}
                        checked={form.singleAccess}
                        onCheckedChange={(checked) =>
                          setForm((f) => ({ ...f, singleAccess: checked }))
                        }
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Button type="submit" variant="primary" disabled={submitting}>
                        {submitting
                          ? t('guestInvitations.form.submitting')
                          : t('guestInvitations.form.submit')}
                      </Button>
                    </div>
                    <div>
                      {createdInvitation && (
                        <LayerCard className="mb-6">
                          <LayerCard.Secondary>
                            <span className="text-sm font-medium">
                              {t('guestInvitations.success.title')}
                            </span>
                          </LayerCard.Secondary>
                          <LayerCard.Primary>
                            <Text
                              as="p"
                              size="sm"
                              DANGEROUS_className="break-all"
                              data-testid="invitation-url"
                            >
                              {createdInvitation.invitationUrl}
                            </Text>
                            <Button
                              className="mt-2"
                              variant="primary"
                              size="sm"
                              onClick={() =>
                                copyLink(createdInvitation.invitationUrl, createdInvitation.id)
                              }
                            >
                              {copiedToken === createdInvitation.id
                                ? t('guestInvitations.success.copied')
                                : t('guestInvitations.success.copyLink')}
                            </Button>
                          </LayerCard.Primary>
                        </LayerCard>
                      )}
                    </div>
                  </form>
                </LayerCard.Primary>
              </LayerCard>
            </GridItem>
          </Grid>
        </>
      )}
    </div>
  )
}
